import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

// Shape-to-object mapping
const shapeObjectMap: { [key: string]: string[] } = {
    "circle.png": ["sun.png", "moon.png", "clock.png", "wheel.png", "ball.png"],
    "square.png": ["chessboard.png", "breadslice.png", "window.png", "photoframe.png", "wallclocksquare.png"],
    "triangle.png": ["pizzaslice.png", "trafficsign.png", "walermelonpiece.png", "sandwitchpiece.png", "christmastree.png"],
    "star.png": ["starfish.png", "starpendent.png", "starballoon.png", "starcookies.png", "starglasses.png"],
    "diamond.png": ["kite.png", "playingcard.png", "diamondbiscuit.png", "diamondbutton.png", "diamondearing.png"],
    "rectangle.png": ["mobilephone.png", "door.png", "rectangleframe.png", "television.png", "rectangleblackboard.png"],
    "pentagon.png": ["house.png", "kennel.png", "pentagontrafficsign.png", "pentagonbutton.png", "pentagonkeyring.png"],
};

interface ImagePair {
    left_id: number;
    right_id: number;
    leftObjects: string;
    rightObjects: string;
}

// Function to shuffle an array
const shuffleArray = (array: any[]) => array.sort(() => Math.random() - 0.5);

const MatchShapesWithObjects: React.FC = () => {
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedShape, setSelectedShape] = useState<{ id: number; x: number; y: number } | null>(null);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate dynamic questions
    const generateQuestions = () => {
        const shuffledShapes = shuffleArray(Object.keys(shapeObjectMap)).slice(0, 4); // Pick 4 random shapes
        const newObjects = shuffledShapes.map((shape, index) => ({
            id: index + 1,
            shape,
            object: shapeObjectMap[shape][Math.floor(Math.random() * shapeObjectMap[shape].length)], // Pick a random object from the values
        }));

        const suffledobject = shuffleArray([...newObjects]);
        const newQuestions: ImagePair[] = [];
        newObjects.map((object, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: newObjects.indexOf(suffledobject[index]) + 1,
                leftObjects: object.shape,
                rightObjects: suffledobject[index].object,
            });
        })

        console.log(newQuestions);

        setQuestions(newQuestions);
        setConnections([]);
        setSelectedShape(null);
    };

    useEffect(() => {
        generateQuestions();
        document.title = "Match Shapes with Objects – UKG Shape Learning | Mama’s Guidance"; // Set page title
    }, []);

    // Get position relative to container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle shape click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedShape({ id, x: position.x, y: position.y });
    };

    // Handle object click
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedShape) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = selectedShape.id === rightId;

            setConnections([...connections, { x1: selectedShape.x, y1: selectedShape.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedShape(null);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help your UKG child identify basic 2D shapes by matching them with real-life objects. Download free worksheets for fun and effective shape learning at home or school." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/shapes/match-shapes-with-object" />
            </Helmet>
            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
            <IonModal
                ref={modal}
                trigger="open-modal"
                initialBreakpoint={1}
                breakpoints={[0, 0.5, 0.75, 1]}
                handleBehavior="cycle"
                backdropDismiss={false}  // ✅ Prevents click-to-close
                className="full-page-modal"
            >
                <IonContent className="ion-padding">
                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                    <h1>Match Shapes with Objects</h1>

                    <p>
                        Understanding shapes is more meaningful when children can connect them to the world around them. This <strong>“Match Shapes with Objects”</strong> activity helps UKG students recognize 2D shapes and relate them to everyday items like clocks, windows, and slices of pizza.
                    </p>

                    <h2>🔍 Why Matching Shapes with Objects Matters</h2>
                    <ul>
                        <li>Develops shape recognition and classification skills</li>
                        <li>Strengthens observation and real-world connections</li>
                        <li>Encourages logical thinking and comparison</li>
                        <li>Prepares children for geometry and spatial awareness</li>
                    </ul>

                    <h2>🟡 Shapes Covered</h2>
                    <ul>
                        <li>Circle – clock, plate, coin</li>
                        <li>Square – gift box, tile, window</li>
                        <li>Triangle – pizza slice, hanger, flag</li>
                        <li>Rectangle – door, book, TV</li>
                        <li>Oval – mirror, balloon, egg</li>
                        <li>Star – starfish, decoration</li>
                        <li>Heart – greeting card, cookie</li>
                    </ul>

                    <h2>📘 Learning Outcomes</h2>
                    <ul>
                        <li>Identify basic 2D shapes visually</li>
                        <li>Match shapes to related real-life items</li>
                        <li>Build foundational math vocabulary</li>
                        <li>Improve attention to detail and memory</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Smallest Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Biggest Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Number Comparison</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🏠 Fun Practice Ideas at Home</h2>
                    <ul>
                        <li>Ask your child to find and name shapes in the kitchen or garden</li>
                        <li>Play “Shape Hunt” using objects like coasters, mirrors, or books</li>
                        <li>Cut paper shapes and find matching household items</li>
                    </ul>

                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                </IonContent>
            </IonModal>
            <article className="visually-hidden">
                <h1>Match Shapes with Objects</h1>

                <p>
                    Understanding shapes is more meaningful when children can connect them to the world around them. This <strong>“Match Shapes with Objects”</strong> activity helps UKG students recognize 2D shapes and relate them to everyday items like clocks, windows, and slices of pizza.
                </p>

                <h2>🔍 Why Matching Shapes with Objects Matters</h2>
                <ul>
                    <li>Develops shape recognition and classification skills</li>
                    <li>Strengthens observation and real-world connections</li>
                    <li>Encourages logical thinking and comparison</li>
                    <li>Prepares children for geometry and spatial awareness</li>
                </ul>

                <h2>🟡 Shapes Covered</h2>
                <ul>
                    <li>Circle – clock, plate, coin</li>
                    <li>Square – gift box, tile, window</li>
                    <li>Triangle – pizza slice, hanger, flag</li>
                    <li>Rectangle – door, book, TV</li>
                    <li>Oval – mirror, balloon, egg</li>
                    <li>Star – starfish, decoration</li>
                    <li>Heart – greeting card, cookie</li>
                </ul>

                <h2>📘 Learning Outcomes</h2>
                <ul>
                    <li>Identify basic 2D shapes visually</li>
                    <li>Match shapes to related real-life items</li>
                    <li>Build foundational math vocabulary</li>
                    <li>Improve attention to detail and memory</li>
                </ul>


                <h2>🔗 Related Topics</h2>
                <div>
                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Smallest Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Biggest Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Number Comparison</IonLabel>
                    </IonItem>
                </div>

                <h2>🏠 Fun Practice Ideas at Home</h2>
                <ul>
                    <li>Ask your child to find and name shapes in the kitchen or garden</li>
                    <li>Play “Shape Hunt” using objects like coasters, mirrors, or books</li>
                    <li>Cut paper shapes and find matching household items</li>
                </ul>
            </article>
            <div ref={containerRef} className="contentPart additionWord matchObject match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                {/* SVG for connections */}
                <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                    {connections.map((conn, index) => (
                        <line key={index} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke={conn.correct ? "green" : "red"} strokeWidth="3" />
                    ))}
                </svg>

                {/* <h6>Match the Shapes with Objects</h6> */}

                <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                    {/* Left Column - Shapes */}
                    <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                        <div className="left-column">
                            {questions.map((item) => (
                                <div key={item.left_id} className={`image-box ${selectedShape?.id === item.left_id ? "selected" : ""}`} onClick={(e) => handleLeftClick(e, item.left_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.leftObjects} />
                                </div>
                            ))}
                        </div>
                        <div className="right-column">
                            {questions.map((item) => (
                                <div key={item.right_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.rightObjects} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="text-center">
                    <button className="btn hero-btn" onClick={generateQuestions}>Next Question</button>
                </div>
            </div>
        </>
    );
};

export default MatchShapesWithObjects;
